import { inject, Injectable } from '@angular/core';
import { SimpleCommand } from '@app-shared/command/async-simple-command.interface';
import { CookieServiceWrapper } from '@app-shared/services/auth/cookie.service';
import { OnboardingService } from '@app-shared/services/onboarding/onboarding.service';
import {
  ContactState,
  Person,
  PersonService,
  PersonStatus,
} from '@app-shared/services/person/person.service';
import {
  ProfileEthic,
  ProfileEthicsService,
} from '@app-shared/services/profile/profile-ethics.service';
import {
  ProfileGoal,
  ProfileGoalsService,
} from '@app-shared/services/profile/profile-goals.service';
import {
  ProfileRisk,
  ProfileRisksService,
} from '@app-shared/services/profile/profile-risks.service';
import { captureException } from '@sentry/angular';
import { EntryParamsService } from '../services/entry-params.service';

export type CreateInitialEntitiesCommandParams = {
  uid: string;
};

@Injectable()
export class CreateInitialEntitiesCommand implements SimpleCommand {
  private onboardingService = inject(OnboardingService);
  private personService = inject(PersonService);
  private profileGoalsService = inject(ProfileGoalsService);
  private profileRisksService = inject(ProfileRisksService);
  private profileEthicsService = inject(ProfileEthicsService);
  private cookieServiceWrapper = inject(CookieServiceWrapper);
  private entryParams = inject(EntryParamsService);


  async execute(params: CreateInitialEntitiesCommandParams): Promise<void> {
    const person = {
      id: params.uid,
      onboarding_id: params.uid,
      state: ContactState.onboarding,
      person_status: PersonStatus.anonymous,
    } as Person;

    // Create Onboarding and Person docs in parallel — they're independent
    try {
      await Promise.all([
        this.onboardingService.initNewOnboardingData(params.uid),
        this.personService.create(person, params.uid),
      ]);
      this.cookieServiceWrapper.setUserCookies(person);
    } catch (err) {
      captureException(`Could not create initial entities ${err}`);
      throw err;
    }

    const seedFundId = this.entryParams.consumePreviousFundId();
    if (seedFundId) {
      this.onboardingService.previous_fund_id = seedFundId;
    }

    // Persist person_id so it survives page refreshes
    this.onboardingService.personId = person.id;
    await this.onboardingService.save();

    // Create profile docs in parallel — all independent, non-blocking failures
    await Promise.all([
      this.profileGoalsService.create({ person_id: person.id } as ProfileGoal)
        .catch((err) => captureException(`Could not create profile goal ${err}`)),
      this.profileRisksService.create({ person_id: person.id } as ProfileRisk)
        .catch((err) => captureException(`Could not create profile risk ${err}`)),
      this.profileEthicsService.create({ person_id: person.id } as ProfileEthic)
        .catch((err) => captureException(`Could not create profile ethics ${err}`)),
    ]);
  }
}
